import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Mail, Monitor, RefreshCw, Smartphone, Sparkles, Zap, ShieldCheck } from "lucide-react";
import { PageHeader } from "@/components/site/kit";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";
import { getPricingConfig } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function getSessionId() {
  let sid = localStorage.getItem("builder_sid");
  if (!sid) {
    sid = `bs-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem("builder_sid", sid);
  }
  return sid;
}

/**
 * AI Project Builder — describe the project, watch a working mini-website
 * generate live in a sandboxed preview, then start the project with a
 * deposit or add a care plan. Free teaser: 3 previews per day per browser.
 */
export default function BuildPreview() {
  const location = useLocation();
  const navigate = useNavigate();
  const { fmt } = useCurrency();
  const passed = location.state || {};

  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    description: passed.description || "",
    business_type: passed.selections?.business_type || "small-business",
    size: passed.selections?.size || "small",
    features: passed.selections?.features || [],
    design: passed.selections?.design || "professional",
  });
  const [phase, setPhase] = useState("idle"); // idle | generating | ready | failed
  const [html, setHtml] = useState("");
  const [estimate, setEstimate] = useState(passed.estimate || null);
  const [previewId, setPreviewId] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [refineText, setRefineText] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadSent, setLeadSent] = useState(false);
  const [leadBusy, setLeadBusy] = useState(false);
  const abortRef = useRef(null);
  const autoRan = useRef(false);

  useEffect(() => { getPricingConfig().then(setCfg).catch(() => {}); }, []);

  // Open a shared preview link: /build?p=<id> (used in the capture emails).
  useEffect(() => {
    const pid = new URLSearchParams(window.location.search).get("p");
    if (!pid || phase !== "idle") return;
    (async () => {
      const doc = await fetch(`${API}/api/builder/preview/${pid}`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
      if (doc?.status === "ready" && doc.html) {
        setHtml(doc.html); setEstimate(doc.estimate); setPreviewId(pid); setPhase("ready");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resume an in-flight preview after a page reload (generation continues
  // server-side regardless).
  useEffect(() => {
    const pid = localStorage.getItem("builder_active");
    if (!pid || phase !== "idle" || passed.autorun) return;
    let cancelled = false;
    (async () => {
      try {
        const doc = await fetch(`${API}/api/builder/preview/${pid}`).then((r) => (r.ok ? r.json() : null));
        if (!doc || cancelled) { localStorage.removeItem("builder_active"); return; }
        if (doc.status === "ready" && doc.html) {
          setHtml(doc.html); setEstimate(doc.estimate); setPreviewId(pid); setPhase("ready");
          localStorage.removeItem("builder_active");
        } else if (doc.status === "generating") {
          setPhase("generating"); setPreviewId(pid);
          const deadline = Date.now() + 5 * 60 * 1000;
          while (Date.now() < deadline && !cancelled) {
            await new Promise((r) => setTimeout(r, 5000));
            const d2 = await fetch(`${API}/api/builder/preview/${pid}`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
            if (d2?.status === "ready" && d2.html) {
              setHtml(d2.html); setEstimate(d2.estimate); setPhase("ready");
              localStorage.removeItem("builder_active");
              break;
            }
            if (d2?.status === "failed") { setPhase("failed"); localStorage.removeItem("builder_active"); break; }
          }
        } else {
          localStorage.removeItem("builder_active");
        }
      } catch { localStorage.removeItem("builder_active"); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFeature = (k) =>
    setForm((f) => ({ ...f, features: f.features.includes(k) ? f.features.filter((x) => x !== k) : [...f.features, k] }));

  const canGenerate = form.description.trim().length >= 10 && phase !== "generating";

  // Stream helper shared by generate + refine: reads SSE events into state.
  const streamEvents = async (res, onStarted) => {
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let raw = "";
    let pid = null;
    let gotDone = false;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n\n");
      buffer = events.pop();
      for (const ev of events) {
        const line = ev.trim();
        if (!line.startsWith("data:")) continue;
        let payload;
        try { payload = JSON.parse(line.slice(5)); } catch { continue; }
        if (payload.started) { pid = payload.id; if (onStarted) onStarted(pid); }
        if (payload.token) {
          raw += payload.token;
          setHtml(raw.replace(/```html|```/g, ""));
        }
        if (payload.error) throw new Error(payload.error);
        if (payload.done) {
          gotDone = true;
          localStorage.removeItem("builder_active");
          if (payload.estimate) setEstimate(payload.estimate);
        }
      }
    }
    return { pid, gotDone };
  };

  const pollUntilReady = async (pid) => {
    const deadline = Date.now() + 5 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 5000));
      const doc = await fetch(`${API}/api/builder/preview/${pid}`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
      if (doc?.status === "ready" && doc.html) {
        setHtml(doc.html);
        if (doc.estimate) setEstimate(doc.estimate);
        localStorage.removeItem("builder_active");
        return true;
      }
      if (doc?.status === "failed") throw new Error("The preview generator hiccuped — please try again.");
    }
    return false;
  };

  const refine = async () => {
    if (refineText.trim().length < 3) {
      toast.error("Type a change first — e.g. 'make it darker' or 'add a menu section'.");
      return;
    }
    if (!previewId || phase === "generating") return;
    setPhase("generating");
    try {
      const res = await fetch(`${API}/api/builder/preview/${previewId}/refine`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instruction: refineText.trim(), session_id: getSessionId() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Refinement failed");
      }
      setHtml("");
      const { pid, gotDone } = await streamEvents(res, () => {});
      if (!gotDone && pid) {
        // Refinements update the SAME preview id — poll it until ready.
        const ok = await pollUntilReady(pid);
        if (!ok) throw new Error("Taking longer than usual — your preview will update here if you wait on this page.");
      }
      setRefineText("");
      setPhase("ready");
    } catch (e) {
      setPhase("ready");
      toast.error(e.message || "Refinement failed — try again.");
    }
  };

  const generate = async () => {
    if (!canGenerate) {
      toast.error("Describe your project first — a sentence or two is enough.");
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase("generating");
    setHtml("");
    setPreviewId(null);
    try {
      const res = await fetch(`${API}/api/builder/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, session_id: getSessionId() }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Generation failed");
      }
      const { pid, gotDone } = await streamEvents(res, (id) => {
        setPreviewId(id);
        localStorage.setItem("builder_active", id);
      });
      // Stream ended. If the proxy cut us off before completion, the server
      // keeps generating in the background — poll until the preview is ready.
      if (!gotDone && pid) await pollUntilReady(pid);
      setPhase("ready");
    } catch (e) {
      if (e.name === "AbortError") return;
      setPhase(html ? "ready" : "failed");
      toast.error(e.message || "Something went wrong — please try again.");
    }
  };

  // Auto-generate when arriving from the quote calculator with a description.
  useEffect(() => {
    if (passed.autorun && !autoRan.current && form.description.trim().length >= 10) {
      autoRan.current = true;
      generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passed.autorun]);

  const startProject = () => {
    navigate("/start-project", {
      state: {
        estimate: estimate || passed.estimate,
        selections: { ...form, maintenance: "none" },
        mode: "deposit",
        preview_id: previewId,
      },
    });
  };

  const wordCount = useMemo(() => html.length, [html]);

  return (
    <div data-testid="build-page">
      <SEO
        title="Website Previewer — See Your Website Before You Buy | Julius Mason"
        description="Describe your project and watch a working preview of your website generate live. Then start the build — free for 30 days, no deposit."
        path="/build"
        lang="en"
        keywords="website preview, custom website design, hire web developer, instant website quote"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Website Previewer", path: "/build" }])}
      />
      <PageHeader
        chapter="Website Previewer"
        titleLines={["Describe it.", "Watch it appear."]}
        description="Tell me about your project in your own words — I'll draft a working preview of your website right here. Like it? We build the real thing together."
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          {/* CONTROLS */}
          <div className="lg:col-span-4 space-y-6" data-testid="builder-controls">
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-2">Describe your project</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={5}
                data-testid="builder-description"
                placeholder="e.g. A cozy coffee roastery in Austin. We sell beans online, do subscriptions, and want a warm, craft feel…"
                className="w-full bg-card border border-border px-4 py-3.5 text-sm leading-relaxed outline-none focus:ring-1 focus:ring-primary resize-y"
              />
            </div>

            {cfg && (
              <>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-2">Business type</label>
                  <div className="grid grid-cols-2 gap-2">
                    {cfg.business_types.map((b) => (
                      <button key={b.key} type="button" data-testid={`builder-type-${b.key}`}
                        onClick={() => setForm((f) => ({ ...f, business_type: b.key }))}
                        className={`text-left border px-3 py-2 text-sm transition-colors ${form.business_type === b.key ? "border-primary bg-primary/5 font-medium" : "border-border bg-card hover:border-primary/50"}`}>
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-2">Size</label>
                  <div className="grid grid-cols-2 gap-2">
                    {cfg.sizes.map((s) => (
                      <button key={s.key} type="button" data-testid={`builder-size-${s.key}`}
                        onClick={() => setForm((f) => ({ ...f, size: s.key }))}
                        className={`text-left border px-3 py-2 text-sm transition-colors ${form.size === s.key ? "border-primary bg-primary/5 font-medium" : "border-border bg-card hover:border-primary/50"}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-2">Features</label>
                  <div className="flex flex-wrap gap-2">
                    {cfg.features.map((f) => (
                      <button key={f.key} type="button" data-testid={`builder-feature-${f.key}`}
                        onClick={() => toggleFeature(f.key)}
                        className={`border px-3 py-1.5 text-xs transition-colors ${form.features.includes(f.key) ? "border-primary bg-primary/5 font-medium" : "border-border bg-card hover:border-primary/50"}`}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-2">Design style</label>
                  <div className="flex flex-wrap gap-2">
                    {cfg.designs.map((d) => (
                      <button key={d.key} type="button" data-testid={`builder-design-${d.key}`}
                        onClick={() => setForm((f) => ({ ...f, design: d.key }))}
                        className={`border px-3 py-1.5 text-xs transition-colors ${form.design === d.key ? "border-primary bg-primary/5 font-medium" : "border-border bg-card hover:border-primary/50"}`}>
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <button
              onClick={generate}
              disabled={!canGenerate}
              data-testid="builder-generate"
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white px-6 py-4 font-semibold hover:bg-primary/90 active:scale-[0.98] transition disabled:opacity-60"
            >
              {phase === "generating" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {phase === "generating" ? "Building your preview…" : phase === "ready" ? "Regenerate Preview" : "Generate My Website Preview"}
            </button>
            <p className="text-xs text-muted-foreground text-center">Free teaser — up to 3 previews a day. No email required.</p>

            {estimate && phase === "ready" && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className="border border-primary/30 bg-card p-6 space-y-4" data-testid="builder-estimate">
                <p className="overline text-primary">Your project estimate</p>
                <div className="flex items-end justify-between">
                  <span className="text-sm text-muted-foreground">Estimated build</span>
                  <span className="font-display text-4xl text-primary" data-testid="builder-total">{fmt(estimate.total)}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Free for 30 days — then {estimate.lease_monthly ? fmt(estimate.lease_monthly) : "—"}/month covers hosting, security, updates and care. No deposit, cancel anytime.
                </p>
                <button onClick={startProject} data-testid="builder-start-project"
                  className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white px-5 py-3.5 font-semibold hover:bg-primary/90 active:scale-[0.98] transition">
                  <Zap className="w-4 h-4" /> Start My Project
                </button>
                <Link to="/pricing" data-testid="builder-care-link"
                  className="w-full inline-flex items-center justify-center gap-2 border border-border px-5 py-3 text-sm font-medium hover:border-primary hover:text-primary transition">
                  <ShieldCheck className="w-4 h-4" /> Add a care plan — first 30 days free
                </Link>
              </motion.div>
            )}
          </div>

          {/* PREVIEW */}
          <div className="lg:col-span-8">
            <div className="border border-border bg-card" data-testid="builder-preview-frame">
              <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-border" />
                  <span className="w-2.5 h-2.5 rounded-full bg-border" />
                  <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="builder-preview-status">
                  {phase === "generating" ? `Generating… ${(wordCount / 1024).toFixed(0)} KB` : phase === "ready" ? "Live preview — click around" : "Your website preview appears here"}
                </p>
                <div className="flex items-center gap-1">
                  <button onClick={() => setDevice("desktop")} data-testid="builder-device-desktop" aria-label="Desktop preview"
                    className={`p-1.5 transition-colors ${device === "desktop" ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                    <Monitor className="w-4 h-4" />
                  </button>
                  <button onClick={() => setDevice("mobile")} data-testid="builder-device-mobile" aria-label="Mobile preview"
                    className={`p-1.5 transition-colors ${device === "mobile" ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                    <Smartphone className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className={`bg-secondary/30 transition-all ${device === "mobile" ? "flex justify-center py-6" : ""}`}>
                {html ? (
                  <iframe
                    title="Website preview"
                    data-testid="builder-iframe"
                    sandbox="allow-scripts"
                    srcDoc={html}
                    className={`bg-white ${device === "mobile" ? "w-[390px] h-[640px] border border-border" : "w-full h-[560px] md:h-[680px]"}`}
                  />
                ) : (
                  <div className="h-[560px] md:h-[680px] flex flex-col items-center justify-center text-center px-8">
                    <Sparkles className="w-10 h-10 text-primary/40 mb-6" />
                    <p className="font-display text-2xl md:text-3xl max-w-md">Your website, drafted live in about a minute.</p>
                    <p className="mt-4 text-sm text-muted-foreground max-w-sm">
                      Describe your project on the left and hit generate. This is a design draft — the real build is handcrafted, fast, and yours.
                    </p>
                  </div>
                )}
              </div>
            </div>
            {phase === "ready" && previewId && (
              <div className="mt-4 border border-border bg-card p-4" data-testid="builder-refine">
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Refine it — tell me what to change</p>
                <div className="flex gap-2">
                  <input
                    value={refineText}
                    onChange={(e) => setRefineText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") refine(); }}
                    placeholder='e.g. "make it darker", "add a menu section", "more playful colors"'
                    aria-label="Refinement instruction"
                    data-testid="builder-refine-input"
                    className="flex-1 bg-background border border-border px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                  />
                  <button onClick={refine} disabled={phase !== "ready"} data-testid="builder-refine-btn"
                    className="inline-flex items-center gap-2 bg-foreground text-background px-5 py-3 text-sm font-semibold hover:opacity-90 active:scale-95 transition disabled:opacity-60">
                    <Sparkles className="w-4 h-4" /> Apply
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["Make it darker", "More playful colors", "Add a pricing section", "Bigger headlines"].map((s) => (
                    <button key={s} onClick={() => setRefineText(s)} data-testid={`builder-refine-chip-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="text-xs border border-border px-3 py-1.5 hover:border-primary hover:text-primary transition-colors">
                      {s}
                    </button>
                  ))}
                </div>

                {/* Lead capture — email the website + pricing */}
                <div className="mt-4 pt-4 border-t border-border" data-testid="builder-lead-capture">
                  {leadSent ? (
                    <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-4 py-3" data-testid="lead-sent-confirmation">
                      Sent! Check your inbox — your website link and pricing are on the way.
                    </p>
                  ) : (
                    <>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Love it? Get this website + pricing in your inbox</p>
                      <form
                        className="flex flex-col sm:flex-row gap-2"
                        onSubmit={async (e) => {
                          e.preventDefault();
                          if (!leadEmail.trim() || leadBusy) return;
                          setLeadBusy(true);
                          try {
                            const res = await fetch(`${API}/api/builder/email`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ email: leadEmail.trim(), preview_id: previewId }),
                            });
                            if (!res.ok) throw new Error();
                            setLeadSent(true);
                          } catch {
                            alert("Couldn't send right now — please try again.");
                          } finally {
                            setLeadBusy(false);
                          }
                        }}
                      >
                        <input
                          type="email"
                          required
                          value={leadEmail}
                          onChange={(e) => setLeadEmail(e.target.value)}
                          placeholder="you@example.com"
                          aria-label="Your email address"
                          data-testid="lead-email-input"
                          className="flex-1 bg-background border border-border px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                        />
                        <button type="submit" disabled={leadBusy} data-testid="lead-email-submit"
                          className="inline-flex items-center justify-center gap-2 bg-primary text-white px-5 py-3 text-sm font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-60">
                          <Mail className="w-4 h-4" /> {leadBusy ? "Sending…" : "Email it to me"}
                        </button>
                      </form>
                    </>
                  )}
                </div>
              </div>
            )}
            {phase === "ready" && (
              <p className="mt-4 text-xs text-muted-foreground flex items-center gap-1.5" data-testid="builder-disclaimer">
                <RefreshCw className="w-3.5 h-3.5 text-primary" />
                Concept preview — your final site is custom-built, refined with you, and fully owned by you.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* HOW IT CONTINUES */}
      <section className="py-16 md:py-24 border-t border-border bg-secondary/20">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid md:grid-cols-3 gap-8">
          {[
            { n: "01", h: "Preview", p: "We draft your homepage so you can feel the direction before spending anything." },
            { n: "02", h: "Refine together", p: "Start your project — free for 30 days. I hand-build the real site and we refine it in writing." },
            { n: "03", h: "Launch & care", p: "We launch inside your free month. Then one simple plan keeps it hosted, secure and updated." },
          ].map((s) => (
            <div key={s.n} className="border border-border bg-card p-8" data-testid={`builder-step-${s.n}`}>
              <p className="font-mono text-xs text-primary mb-4">{s.n}</p>
              <h3 className="font-display text-2xl mb-3">{s.h}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.p}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
